// @flow
import * as React from 'react';
import UserGeneratedTextContainer from './UserGeneratedTextContainer';
import StarRating from 'components/Common/StarRating';
import startCase from 'lodash/startCase';
import MediaViewer from './MediaViewer';
import {
  VerifiedBuyer,
  VerifiedReviewer,
  StaffReviewer,
  ExpertReviewer,
} from 'components/Icons/Badges';
import type { UGCSection, UGCSectionAttribute } from './UGCSection';
import AuthorInfo from './AuthorInfo';
import { contentSectionHeader as header } from './ContentSection.scss';
import styles from './ReviewSection.scss';
import type { Review, ClientSpecificQuestion } from '../../models';
import AttributeList from './AttributeList';
import type { List } from 'immutable';
import type { AttributeListItem } from './AttributeList';

function getTagAttributes(tags: UgcTag[]): Array<UGCSectionAttribute> {
  const sep = ', ';
  const out = [];

  if (!tags) return [];

  tags.forEach(tag => {
    const all = [];
    const { label, selected = [], user_added = [] } = tag;

    selected.forEach(v => all.push(sep, v));
    user_added.forEach(v =>
      all.push(sep, <UserGeneratedTextContainer key={v} text={v} />)
    );
    all.shift();

    if (all.length > 0) {
      out.push({
        key: label,
        label,
        value: all,
      });
    }
  });

  return out;
}

function getClientSpecificQuestionAttributes(
  csqs: List<ClientSpecificQuestion>
): Array<AttributeListItem> {
  const attributes = [];

  if (!csqs) return attributes;

  const visibleCsqs = csqs.filter(({ hidden }) => hidden === false);
  const hiddenCsqs = csqs.filter(({ hidden }) => hidden === true);

  const questionToAttributeItem = csq => {
    const { label, selected, userAdded } = csq;
    const sep = ', ';
    const value = [];

    selected.forEach(s => {
      value.push(sep, s);
    });
    userAdded.forEach(s => {
      value.push(sep, <UserGeneratedTextContainer key={s} text={s} />);
    });
    value.shift();

    attributes.push({
      key: label,
      label,
      value,
    });
  };

  visibleCsqs.forEach(questionToAttributeItem);

  if (hiddenCsqs.size) {
    attributes.push('Hidden Questions');
    hiddenCsqs.forEach(questionToAttributeItem);
  }

  return attributes;
}

export function getAttributes(
  ugc: Review,
  options: { program: AppProgram }
): AttributeListItem[] {
  const { tags, service_comments, clientSpecificQuestions, bottom_line } = ugc;
  const isClientCard = options.program === ('client': AppProgram);
  const clientSpecificQuestionsForCardType = isClientCard
    ? clientSpecificQuestions
    : clientSpecificQuestions.filter(({ hidden }) => hidden === false);

  const attributes = [
    ...getTagAttributes(tags),
    ...getClientSpecificQuestionAttributes(clientSpecificQuestionsForCardType),
  ];

  if (bottom_line != null) {
    const bottomLineItem: AttributeListItem = {
      key: 'bottom_line',
      label: 'Would Recommend to a Friend',
      value: bottom_line === true ? 'Yes' : 'No',
    };
    attributes.push(bottomLineItem);
  }

  if (isClientCard && service_comments) {
    const serviceCommentItem: AttributeListItem = {
      key: 'service_comments',
      label: 'Service Comments',
      value: <UserGeneratedTextContainer text={service_comments} />,
    };

    const indexOfFirstGroupLabel = attributes.findIndex(
      v => typeof v === 'string'
    );

    // If MSQs are being grouped, service comments should appear before any groups
    if (indexOfFirstGroupLabel > -1) {
      attributes.splice(indexOfFirstGroupLabel, 0, serviceCommentItem);
    } else {
      attributes.push(serviceCommentItem);
    }
  }

  return attributes;
}

const ReviewSection: UGCSection = {
  Header: props => {
    const {
      ugc,
      ugc: { rating, review_headline, reviewer_type },
    } = props;
    const reviewerType =
      ugc.source === 'email' ? 'verified_buyer' : reviewer_type;

    return (
      <div>
        <div className="flex flex-row items-center justify-between mb2">
          {typeof rating === 'number' && (
            <div className="flex items-stretch ReviewSection__h1">
              <StarRating rating={rating} />
              <span
                className="ba b--moon-gray br1 flex items-center lh-solid f6 mid-gray justify-center ReviewSection__h1 ReviewSection__w1"
                data-key="rating"
              >
                {rating}
              </span>
            </div>
          )}
          {renderReviewBadge(reviewerType)}
        </div>
        <h2 className={header}>
          {review_headline && (
            <UserGeneratedTextContainer
              data-key="review_headline"
              text={review_headline}
            />
          )}
        </h2>
        <AuthorInfo ugc={ugc} />
      </div>
    );
  },

  Content: props => {
    const {
      ugc: { review_comments },
    } = props;

    return review_comments ? (
      <p className={`${styles.reviewSectionComments} ma4 mt0`}>
        <UserGeneratedTextContainer
          data-key="review_comments"
          text={review_comments}
        />
      </p>
    ) : null;
  },

  getRelatedContent: (ugc: Review, options) => {
    const { program } = options;
    const showRelatedMedia = program === 'client' || program === 'brand';
    const attributes = getAttributes(ugc, options);

    const relatedContent = [];

    if (attributes.length) {
      relatedContent.push(
        <div key="attributes" className="ma4 mt0">
          <AttributeList items={attributes} />
        </div>
      );
    }

    if (showRelatedMedia) {
      const mediaUgcs = ugc.getMedia();

      if (mediaUgcs.size) {
        relatedContent.push(
          <div className="ma4" key="media">
            <MediaViewer
              ugcs={ugc.getMedia()}
              hideUgcLink={program !== 'client'}
            />
          </div>
        );
      }
    }

    return relatedContent;
  },
};

function renderReviewBadge(type) {
  let IconComponent;

  if (type === 'verified_buyer') {
    IconComponent = VerifiedBuyer;
  } else if (type === 'verified_reviewer') {
    IconComponent = VerifiedReviewer;
  } else if (type === 'staff_reviewer') {
    IconComponent = StaffReviewer;
  } else if (type === 'staff_expert') {
    IconComponent = ExpertReviewer;
  }

  return IconComponent ? (
    <div className={styles.reviewSectionBadge}>
      <IconComponent className={styles.reviewSectionBadgeIcon} />
      <span className={styles.reviewSectionBadgeLabel}>{startCase(type)}</span>
    </div>
  ) : null;
}

export default ReviewSection;



// WEBPACK FOOTER //
// ./src/components/ContentSection/ReviewSection.js